import React from "react";
import { EntityEditProvider } from "@/contexts/EntityEditContext";
import type { EntityEditProviderProps } from "@/types/contexts/entityEdit";
import { TimecardType } from "@/types/db/time_cards";
import { timecardService } from "@/services/timecardService";

interface TimecardEditProviderProps extends Omit<EntityEditProviderProps<TimecardType>, "onSave" | "entityName"> {
  children: React.ReactNode;
}

export function TimecardEditProvider({ children, entityData, loading, onSaveSuccess }: TimecardEditProviderProps) {
  const handleSave = async (formData: Partial<TimecardType>): Promise<TimecardType> => {
    if (!entityData?.id) {
      throw new Error("No timecard ID available");
    }

    // Clean the data - remove read-only fields before sending to database
    // `user` is a joined relation present at runtime but not part of TimecardType.
    const { id, created_at, updated_at, user, ...cleanFormData } = formData as Partial<TimecardType> & {
      user?: unknown;
      created_at?: unknown;
      updated_at?: unknown;
    };

    // Use saveRowPatches for single timecard update
    await timecardService.saveRowPatches([{ id: entityData.id, ...cleanFormData }]);

    // Return updated data - we'll need to refetch or construct the response
    return { ...entityData, ...cleanFormData } as TimecardType;
  };

  return (
    <EntityEditProvider
      entityData={entityData}
      loading={loading}
      onSave={handleSave}
      onSaveSuccess={onSaveSuccess}
      entityName="Timecard"
    >
      {children}
    </EntityEditProvider>
  );
}
